import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import cn from 'classnames';
import { compact } from 'lodash-es';
import { MAX_PAYMENT_ATTEMPTS } from 'anchor-scout-common/types';
import { FullPageModalHeaderStart, Typography, Badge, ConfirmationModal, Callout, Button, useOverlayContext, useDevice } from 'seagull';
import { getStatusLabel, invoiceLogicTextsPath } from '~/business-logic/invoiceLogic';
import { InvoiceAccountingIntegrationStatus } from '~/components/InvoiceAccountingIntegrationStatus';
import { routes } from '~/constants';
import { useEventReporter, useIoContext, useStore, useStoreActions, useStoreStatic } from '~/contexts';
import { useUnmarkPaidExternally } from '~/hooks/invoice/useUnmarkPaidExternally';
import { useUnvoidInvoice } from '~/hooks/invoice/useUnvoidInvoice';
import { useInvoiceUpdatedEvent } from '~/hooks/pusher/useInvoiceUpdatedEvent';
import { useGetInvoice } from '~/hooks/reactQuery/queries/useInvoice';
import { useAttachFiles } from '~/hooks/useAttachFiles';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { handlePromise, handleRequest } from '~/infra/utils';
import { invoiceModel, relationshipModel, timeModel } from '~/models';
import { makeManualApprovalMethodNoticePeriod } from '~/models/approval-method-notice-period';
import { InvoiceOptimisticUpdateType } from '~/providers/InvoiceChangesObservableProvider';
import { selectors } from '~/store/state-selectors';
import { Accounting } from '~/types/api.model';
import { Attachments } from '../Attachments/Attachments';
import styles from './FullPageInvoice.module.scss';
import { MobileDivider, getBadgeSkin, getFooter, onRequestPartialRefundSuccess, onRequestPaymentConfirmed, showRequestPaymentMethodModal, utilsTextsPath } from './FullPageInvoice.utils';
import FullPageInvoiceAccountingCallout from './FullPageInvoiceAccountingCallout';
import FullPageInvoiceAdditionalActions from './FullPageInvoiceAdditionalActions';
import { calloutTextsPath, FullPageInvoiceCallout, getFullPageInvoiceCallouts, PaymentFailedActions, ResolvePaymentCallout } from './FullPageInvoiceCallout';
import InvoiceLineItemsTable from './InvoiceLineItemsTable/InvoiceLineItemsTable';
import RequestPartialRefund from './RequestPartialRefund';
import StopPaymentModal from './StopPaymentModal';
const approvePaymentLoader = 'approve-payment';
const retryChargeLoader = 'retry-charge';
const payNowLoader = 'pay-now';
const stopPaymentLoader = 'stopPayment';
const resumePaymentLoader = 'resumePayment';
export default function FullPageInvoiceWithData(props) {
    const { relationship, isVendor, showQboIntegration, showInvoiceQboSection, showInvoiceXeroSection, showXeroIntegration, contacts, serviceTemplates, refresh, onRefresh, onRedirectToPaymentMethods, hideDropdownActions } = props;
    const { t } = useLocaleKeys();
    const { t: fullPageTexts } = useLocaleKeys('invoice.fullPage');
    const { t: commonButtons } = useLocaleKeys('common.buttons');
    const { t: calloutTexts } = useLocaleKeys(calloutTextsPath);
    const { t: utilsTexts } = useLocaleKeys(utilsTextsPath);
    const { t: invoiceLogicTexts } = useLocaleKeys(invoiceLogicTextsPath);
    const { isMobileDevice } = useDevice();
    const overlayContext = useOverlayContext();
    const { api, logBI } = useStoreActions();
    const history = useHistory();
    const { reportButtonClick } = useEventReporter();
    const { getNow } = useIoContext();
    const now = useMemo(()=>getNow(), []);
    const { getState } = useStoreStatic();
    const [invoice, setInvoice] = useState(props.invoice);
    const [disablingAutoApprove, setDisablingAutoApprove] = useState(false);
    const { startUnvoidInvoice, finishUnvoidInvoice } = useUnvoidInvoice();
    const { startUnmarkPaidExternally, finishUnmarkPaidExternally } = useUnmarkPaidExternally();
    const { attachments, openFilePicker, handleRemove: handleRemoveAttachment, isUploading: isUploadingAttachments } = useAttachFiles({
        initialAttachments: invoice.attachments,
        fetchAttachmentUrl: (attachmentId)=>api.invoice.getAttachmentDownloadUrl(invoice.id, attachmentId),
        onFilesUploaded: (uploadedData)=>api.invoice.attachFiles(invoice.id, uploadedData),
        onRemove: (attachment)=>api.invoice.removeAttachment(invoice.id, attachment.id)
    });
    const isPendingQBOIntegrationSetup = useStore(selectors.integration.isPendingAccountingIntegrationSetup(Accounting.QBO));
    const isPendingXeroIntegrationSetup = useStore(selectors.integration.isPendingAccountingIntegrationSetup(Accounting.Xero));
    const { name, postalAddress } = relationship.vendor;
    const vendorLogoUrl = relationshipModel.vendorLogoUrl(relationship);
    const { addressOne, addressTwo, city, state, postalCode } = postalAddress;
    const { AD_HOC_INVOICING } = useFeatureFlags();
    const isAdHoc = !!(AD_HOC_INVOICING && invoice.isAdHoc);
    useEffect(()=>{
        setInvoice(props.invoice);
    }, [
        props.invoice
    ]);
    async function reloadInvoice() {
        refresh().then(()=>onRefresh === null || onRefresh === void 0 ? void 0 : onRefresh({}));
    }
    const onDisableAutoApprove = async (invoice)=>{
        setDisablingAutoApprove(true);
        try {
            await api.invoice.disableAutoApprove(invoiceModel.id(invoice));
            overlayContext.showSuccessToast(fullPageTexts.disabledAutoApprovalSuccess(), 'disable-auto-approval-success');
            setInvoice(invoiceModel.setApprovalMethod(invoice, makeManualApprovalMethodNoticePeriod()));
        } catch (unused) {
            overlayContext.showErrorToast(fullPageTexts.failedToDisableAutoApproval(), 'disable-auto-approval-error');
        } finally{
            setDisablingAutoApprove(false);
        }
    };
    function onRetryCharge() {
        overlayContext.showModal({
            biName: 'full_page_invoice_with_data_full_page_retry_charge_confirm',
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ConfirmationModal, {
                    bi: {
                        primaryButton: {
                            name: 'full_page_invoice_with_data_1_confirm'
                        },
                        secondaryButton: {
                            name: 'full_page_invoice_with_data_1_cancel'
                        }
                    },
                    skin: "danger",
                    title: fullPageTexts.retryChargeTitle(),
                    textAlignCenter: false,
                    content: /*#__PURE__*/ _jsx(_Fragment, {
                        children: /*#__PURE__*/ _jsxs(Typography, {
                            testId: "retry-charge-modal-text",
                            children: [
                                t.invoice.retryCharge.retryLimitDescription({
                                    count: MAX_PAYMENT_ATTEMPTS
                                }),
                                ' ',
                                isVendor && fullPageTexts.retryChargeVendorRecommendation(),
                                /*#__PURE__*/ _jsx("br", {}),
                                /*#__PURE__*/ _jsx("br", {}),
                                fullPageTexts.retryChargeConfirmation({
                                    invoiceNumber: invoice.formattedNumber
                                })
                            ]
                        })
                    }),
                    onPrimaryButtonClick: async ()=>{
                        overlayContext.startLoader(retryChargeLoader);
                        const { success } = await handleRequest(api.invoice.retryCharge(invoice.id));
                        if (!success) {
                            overlayContext.showErrorToast(fullPageTexts.unableToRetry(), 'retry-charge-error');
                            overlayContext.stopLoader(retryChargeLoader);
                            return;
                        }
                        onRequestPaymentConfirmed({
                            invoice,
                            setInvoice,
                            onRefresh,
                            isVendor
                        });
                        overlayContext.stopLoader(retryChargeLoader);
                        dismissModal();
                    },
                    onSecondaryButtonClick: ()=>dismissModal(),
                    onCloseButtonClick: ()=>dismissModal(),
                    testId: "retry-charge-confirmation-modal",
                    primaryButtonLabel: fullPageTexts.chargeInvoiceButton({
                        invoiceNumber: invoice.formattedNumber
                    }),
                    primaryButtonIntent: Intent.InvoiceRetryCharge,
                    secondaryButtonLabel: commonButtons.cancel()
                });
            }
        });
    }
    async function onApprovePayment() {
        logBI(10564, {
            invoiceId: invoice.id,
            relationshipId: relationship.id
        });
        reportButtonClick('billing-hub-client-approved-payment');
        overlayContext.startLoader(approvePaymentLoader);
        const { success } = await handleRequest(api.invoice.approve(invoice.id));
        if (success) {
            // Optimistic update.
            setInvoice(invoiceModel.setPending(invoice, now));
            onRefresh === null || onRefresh === void 0 ? void 0 : onRefresh({
                optimisticUpdate: {
                    type: InvoiceOptimisticUpdateType.PaymentApproved,
                    invoiceId: invoice.id
                }
            });
        } else {
            overlayContext.showErrorToast(fullPageTexts.failedToApproveInvoice({
                invoiceNumber: invoice.formattedNumber
            }), 'approve-payment-error');
        }
        overlayContext.stopLoader(approvePaymentLoader);
    }
    async function onPayNow() {
        overlayContext.showModal({
            biName: 'full_page_invoice_with_data_full_page_pay_now_confirm',
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ConfirmationModal, {
                    bi: {
                        primaryButton: {
                            name: 'full_page_invoice_with_data_2_confirm'
                        },
                        secondaryButton: {
                            name: 'full_page_invoice_with_data_2_cancel'
                        }
                    },
                    textAlignCenter: false,
                    onPrimaryButtonClick: async ()=>{
                        logBI(10561, {
                            invoiceId: invoice.id
                        });
                        overlayContext.startLoader(payNowLoader);
                        const { success } = await handlePromise(api.invoice.payNow(invoice.id));
                        if (!success) {
                            overlayContext.showErrorToast(fullPageTexts.unableToPayNow(), 'pay-now-error');
                            overlayContext.stopLoader(payNowLoader);
                            return;
                        }
                        await handleRequest(reloadInvoice());
                        overlayContext.stopLoader(payNowLoader);
                        dismissModal();
                    },
                    onCloseButtonClick: ()=>dismissModal(),
                    onSecondaryButtonClick: ()=>dismissModal(),
                    title: fullPageTexts.payNowTitle(),
                    content: /*#__PURE__*/ _jsxs(Typography, {
                        children: [
                            fullPageTexts.payNowContent({
                                date: timeModel.formatDate(now.getTime())
                            }),
                            /*#__PURE__*/ _jsx("br", {}),
                            /*#__PURE__*/ _jsx("br", {}),
                            fullPageTexts.payNowCancelHint({
                                dueDate: invoiceModel.dueDate(invoice)
                            })
                        ]
                    }),
                    primaryButtonLabel: fullPageTexts.iAgree(),
                    primaryButtonTestId: "invoice-pay-now-confirm",
                    secondaryButtonLabel: commonButtons.cancel()
                });
            }
        });
    }
    function onStopPayment() {
        overlayContext.showModal({
            biName: 'stop_payment_modal',
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: (props)=>/*#__PURE__*/ _jsx(StopPaymentModal, _object_spread_props(_object_spread({}, props), {
                    formattedNumber: invoice.formattedNumber,
                    onSubmit: async (reason)=>{
                        const logStopPaymentBI = (success)=>logBI(10562, {
                                invoiceId: invoice.id,
                                success
                            });
                        overlayContext.startLoader(stopPaymentLoader);
                        const { success } = await handleRequest(api.invoice.stopPayment(invoice.id, reason));
                        if (!success) {
                            logStopPaymentBI(false);
                            overlayContext.stopLoader(stopPaymentLoader);
                            overlayContext.showErrorToast(fullPageTexts.failedToStopPayment({
                                invoiceNumber: invoice.formattedNumber
                            }), 'fpi-stop-payment-error');
                            return {
                                success: false
                            };
                        } else {
                            logStopPaymentBI(true);
                            await handleRequest(reloadInvoice());
                            overlayContext.stopLoader(stopPaymentLoader);
                            return {
                                success: true
                            };
                        }
                    }
                }))
        });
    }
    function onResumePayment() {
        overlayContext.showModal({
            biName: 'full_page_invoice_with_data_full_page_resume_payment_confirm',
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ConfirmationModal, {
                    bi: {
                        primaryButton: {
                            name: 'full_page_invoice_with_data_3_confirm'
                        },
                        secondaryButton: {
                            name: 'full_page_invoice_with_data_3_cancel'
                        }
                    },
                    title: fullPageTexts.resumePaymentTitle(),
                    textAlignCenter: false,
                    primaryButtonLabel: fullPageTexts.resumeButton(),
                    primaryButtonTestId: "submit-resume-payment",
                    secondaryButtonLabel: commonButtons.back(),
                    content: /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                children: fullPageTexts.resumePaymentDescription({
                                    invoiceNumber: invoice.formattedNumber
                                })
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                children: fullPageTexts.resumePaymentFutureInvoices()
                            })
                        ]
                    }),
                    onSecondaryButtonClick: ()=>dismissModal(),
                    onPrimaryButtonClick: async ()=>{
                        const logResumePaymentBI = (success)=>logBI(10563, {
                                invoiceId: invoice.id,
                                success
                            });
                        overlayContext.startLoader(resumePaymentLoader);
                        const { success } = await handleRequest(api.invoice.resumePayment(invoice.id));
                        if (!success) {
                            logResumePaymentBI(false);
                            overlayContext.stopLoader(resumePaymentLoader);
                            overlayContext.showErrorToast(fullPageTexts.failedToResumePayment({
                                invoiceNumber: invoice.formattedNumber
                            }), 'fpi-resume-payment-error');
                        } else {
                            logResumePaymentBI(true);
                            await handleRequest(reloadInvoice());
                            overlayContext.stopLoader(resumePaymentLoader);
                        }
                        dismissModal();
                    }
                });
            }
        });
    }
    async function onVoidInvoice() {
        var _getState_userInfo;
        const confirmed = await overlayContext.showModal({
            biName: 'full_page_invoice_with_data_full_page_void_confirm',
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ConfirmationModal, {
                    bi: {
                        primaryButton: {
                            name: 'full_page_invoice_with_data_4_confirm'
                        },
                        secondaryButton: {
                            name: 'full_page_invoice_with_data_4_cancel'
                        }
                    },
                    title: fullPageTexts.voidTitle(),
                    testId: "fpi-void-invoice-modal",
                    content: fullPageTexts.voidConfirmation({
                        invoiceNumber: invoice.formattedNumber
                    }),
                    onPrimaryButtonClick: ()=>dismissModal(true),
                    onSecondaryButtonClick: ()=>dismissModal(false),
                    primaryButtonLabel: commonButtons.imSure(),
                    primaryButtonTestId: "fpi-void-invoice-confirm",
                    secondaryButtonLabel: commonButtons.cancel()
                });
            }
        });
        if (!confirmed) return;
        const loaderName = 'void';
        try {
            overlayContext.startLoader(loaderName);
            await api.invoice.void(invoice.id);
        } catch (err) {
            overlayContext.showErrorToast(fullPageTexts.failedToVoidInvoice({
                invoiceNumber: invoice.formattedNumber
            }), 'fpi-void-invoice-error');
            return;
        } finally{
            overlayContext.stopLoader(loaderName);
        }
        // Optimistic update.
        const userId = ((_getState_userInfo = getState().userInfo) === null || _getState_userInfo === void 0 ? void 0 : _getState_userInfo.id()) || '';
        setInvoice(invoiceModel.setVoided(invoice, userId));
        onRefresh === null || onRefresh === void 0 ? void 0 : onRefresh({
            optimisticUpdate: {
                type: InvoiceOptimisticUpdateType.Voided,
                invoiceId: invoice.id,
                userId
            }
        });
    }
    function onRequestRefund() {
        const payment = invoiceModel.payment(invoice);
        if (!payment) return;
        overlayContext.showModal({
            biName: 'request_partial_refund_modal',
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(RequestPartialRefund, {
                    relationship: relationship,
                    id: invoice.id,
                    rawTotalAmount: invoiceModel.rawTotalAmount(invoice),
                    dismissModal: dismissModal,
                    payment: payment,
                    refreshInvoice: refresh,
                    onSuccess: (param)=>{
                        let { reason, refundAmount, refundFees } = param;
                        return onRequestPartialRefundSuccess({
                            invoice,
                            sellerId: relationshipModel.sellerID(relationship),
                            reason,
                            now: getNow(),
                            setInvoice,
                            onRefresh,
                            refundAmount,
                            refundFees
                        });
                    }
                });
            }
        });
    }
    function handleQboIntegrationChange(integration) {
        onRefresh === null || onRefresh === void 0 ? void 0 : onRefresh({
            optimisticUpdate: {
                type: InvoiceOptimisticUpdateType.QboIntegrationChanged,
                invoiceId: invoice.id,
                integration
            }
        });
    }
    function handleXeroIntegrationChange(integration) {
        onRefresh === null || onRefresh === void 0 ? void 0 : onRefresh({
            optimisticUpdate: {
                type: InvoiceOptimisticUpdateType.XeroIntegrationChanged,
                invoiceId: invoice.id,
                xeroIntegration: integration
            }
        });
    }
    const getInvoice = useGetInvoice();
    useInvoiceUpdatedEvent({
        onInvoiceRefetch: async (invoiceId)=>{
            if (invoice.id !== invoiceId) return;
            const updated = await getInvoice(invoiceId);
            setInvoice(updated);
        },
        onUnvoided: (invoiceId)=>{
            if (invoice.id === invoiceId) finishUnvoidInvoice(invoiceId);
        },
        onUnmarkedAsPaidExternally: (invoiceId)=>{
            if (invoice.id === invoiceId) finishUnmarkPaidExternally(invoiceId);
        }
    });
    return /*#__PURE__*/ _jsxs("div", {
        className: cn(styles.root, {
            [styles.mobile]: isMobileDevice
        }),
        "data-testid": "fpi-content",
        children: [
            /*#__PURE__*/ _jsx(FullPageModalHeaderStart, {
                children: /*#__PURE__*/ _jsxs("div", {
                    className: styles.invoiceHeader,
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: styles.invoiceNumber,
                            children: fullPageTexts.invoiceHeader({
                                invoiceNumber: invoice.formattedNumber
                            })
                        }),
                        /*#__PURE__*/ _jsx(Badge, {
                            outlined: true,
                            rounded: true,
                            skin: getBadgeSkin(invoiceModel.displayStatus(invoice)),
                            testId: "fpi-status",
                            "data-intent": invoiceModel.displayStatus(invoice),
                            children: getStatusLabel(invoiceModel.displayStatus(invoice), invoiceLogicTexts.displayedStatusLabels)
                        }),
                        showInvoiceQboSection && /*#__PURE__*/ _jsx(InvoiceAccountingIntegrationStatus, {
                            className: styles.qboIcon,
                            testId: "fpi-qbo-icon",
                            invoiceIntegration: invoiceModel.qboIntegration(invoice),
                            placement: "right",
                            accountingType: Accounting.QBO
                        }),
                        showInvoiceXeroSection && /*#__PURE__*/ _jsx(InvoiceAccountingIntegrationStatus, {
                            className: styles.xeroIcon,
                            testId: "fpi-xero-icon",
                            invoiceIntegration: invoiceModel.xeroIntegration(invoice),
                            placement: "right",
                            accountingType: Accounting.Xero
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: styles.calloutsSection,
                children: [
                    showRequestPaymentMethodModal(invoice, relationship) && /*#__PURE__*/ _jsx(Callout, {
                        intent: "warning",
                        testId: "fpi-missing-pm",
                        withAction: /*#__PURE__*/ _jsx(Button, {
                            bi: {
                                name: 'invoice_add_payment_method'
                            },
                            skin: "primaryOutline",
                            testId: "fpi-add-pm",
                            onClick: onRedirectToPaymentMethods,
                            children: fullPageTexts.addPaymentMethod()
                        }),
                        children: fullPageTexts.paymentMethodRequired()
                    }),
                    invoiceModel.isPaymentMethodUnverified(invoice) && !relationshipModel.isTerminated(relationship) && /*#__PURE__*/ _jsx(Callout, {
                        intent: "warning",
                        testId: "fpi-unverified-pm",
                        withAction: /*#__PURE__*/ _jsx(Button, {
                            bi: {
                                name: 'invoice_verify_payment_method'
                            },
                            skin: "primaryOutline",
                            testId: "fpi-verify-pm",
                            onClick: onRedirectToPaymentMethods,
                            children: fullPageTexts.verifyPaymentMethod()
                        }),
                        children: fullPageTexts.paymentMethodUnverified()
                    }),
                    isPendingQBOIntegrationSetup && showQboIntegration && /*#__PURE__*/ _jsx(Callout, {
                        intent: "warning",
                        testId: "fpi-qbo-setup",
                        withAction: /*#__PURE__*/ _jsx(Button, {
                            bi: {
                                name: 'invoice_go_to_qbo_setup'
                            },
                            testId: "fpi-go-to-qbo-setup",
                            skin: "primaryOutline",
                            onClick: ()=>history.push(routes.qboSetup),
                            children: fullPageTexts.goToSetup()
                        }),
                        children: fullPageTexts.qboSetupMessage()
                    }),
                    isPendingXeroIntegrationSetup && showXeroIntegration && /*#__PURE__*/ _jsx(Callout, {
                        intent: "warning",
                        testId: "fpi-xero-setup",
                        withAction: /*#__PURE__*/ _jsx(Button, {
                            bi: {
                                name: 'invoice_go_to_xero_setup'
                            },
                            testId: "fpi-go-to-xero-setup",
                            skin: "primaryOutline",
                            onClick: ()=>history.push(routes.xeroSetup),
                            children: fullPageTexts.goToSetup()
                        }),
                        children: fullPageTexts.xeroSetupMessage()
                    }),
                    showInvoiceQboSection && !isMobileDevice && /*#__PURE__*/ _jsx(FullPageInvoiceAccountingCallout, {
                        relationship: relationship,
                        invoice: invoice,
                        contacts: contacts,
                        serviceTemplates: serviceTemplates,
                        reloadInvoice: reloadInvoice,
                        onChange: handleQboIntegrationChange,
                        accounting: Accounting.QBO,
                        integration: invoiceModel.accounting({
                            accounting: Accounting.QBO,
                            invoice
                        }).integration
                    }),
                    showInvoiceXeroSection && !isMobileDevice && /*#__PURE__*/ _jsx(FullPageInvoiceAccountingCallout, {
                        relationship: relationship,
                        invoice: invoice,
                        contacts: contacts,
                        serviceTemplates: serviceTemplates,
                        reloadInvoice: reloadInvoice,
                        onChange: handleXeroIntegrationChange,
                        accounting: Accounting.Xero,
                        integration: invoiceModel.accounting({
                            accounting: Accounting.Xero,
                            invoice
                        }).integration
                    }),
                    getFullPageInvoiceCallouts({
                        invoice,
                        relationship,
                        isVendor,
                        isAdHoc,
                        disabling: disablingAutoApprove,
                        now,
                        onApprovePayment,
                        onPayNow,
                        onResumePayment,
                        onDisableAutoApprove
                    }, calloutTexts).map((calloutProps)=>/*#__PURE__*/ _jsx(FullPageInvoiceCallout, _object_spread_props(_object_spread({}, calloutProps), {
                            actionSlot: calloutProps.id === 'payment-failed' ? /*#__PURE__*/ _jsx(PaymentFailedActions, {
                                invoice: invoice,
                                relationship: relationship,
                                isVendor: isVendor,
                                isTerminated: relationshipModel.isTerminated(relationship),
                                onRetryCharge: onRetryCharge
                            }) : undefined
                        }), calloutProps.id)),
                    invoiceModel.isPaymentActionRequired(invoice) && /*#__PURE__*/ _jsx(ResolvePaymentCallout, {
                        invoice: invoice,
                        isVendor: isVendor,
                        reloadInvoice: reloadInvoice
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: styles.titleSection,
                children: [
                    !isMobileDevice && /*#__PURE__*/ _jsx(Typography, {
                        variant: "h2",
                        children: fullPageTexts.invoiceTitle()
                    }),
                    vendorLogoUrl && /*#__PURE__*/ _jsx("img", {
                        className: styles.logo,
                        src: vendorLogoUrl
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: styles.titleSection,
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: styles.invoiceDetails,
                        children: [
                            [
                                fullPageTexts.clientNameLabel(),
                                relationship.customer.name
                            ],
                            [
                                fullPageTexts.invoiceNumberLabel(),
                                invoiceModel.formattedNumber(invoice)
                            ],
                            [
                                fullPageTexts.issueDateLabel(),
                                invoiceModel.issuedOn(invoice)
                            ],
                            [
                                fullPageTexts.dueDateLabel(),
                                invoiceModel.dueDate(invoice)
                            ]
                        ].map((param, idx)=>{
                            let [label, value] = param;
                            return /*#__PURE__*/ _jsxs(Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx(Typography, {
                                        children: label
                                    }, idx + 'l'),
                                    /*#__PURE__*/ _jsx(Typography, {
                                        children: value
                                    }, idx + 'v')
                                ]
                            }, idx);
                        })
                    }),
                    isMobileDevice && /*#__PURE__*/ _jsx(MobileDivider, {}),
                    /*#__PURE__*/ _jsx("div", {
                        className: styles.vendorDetails,
                        children: compact([
                            name,
                            addressOne,
                            addressTwo,
                            "".concat(city, ", ").concat(state, ", ").concat(postalCode)
                        ]).map((text, idx)=>/*#__PURE__*/ _jsx(Typography, {
                                children: text
                            }, idx))
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(InvoiceLineItemsTable, {
                invoice: invoice,
                isVendor: isVendor
            }),
            attachments.length > 0 && /*#__PURE__*/ _jsx(Attachments, {
                files: attachments,
                onRemove: handleRemoveAttachment
            }),
            getFooter(relationship, invoice, utilsTexts).map((param)=>{
                let { id, text } = param;
                return /*#__PURE__*/ _jsx(Typography, {
                    variant: "paragraph2",
                    children: text
                }, id);
            }),
            /*#__PURE__*/ _jsx(FullPageInvoiceAdditionalActions, {
                invoice: invoice,
                relationship: relationship,
                isVendor: isVendor,
                reloadInvoice: reloadInvoice,
                onVoidInvoice: onVoidInvoice,
                onUnvoidInvoice: ()=>startUnvoidInvoice(invoice),
                onUnmarkPaidExternally: ()=>startUnmarkPaidExternally(invoice),
                onStopPayment: onStopPayment,
                onResumePayment: onResumePayment,
                onRequestRefund: onRequestRefund,
                onAttachFilesClick: openFilePicker,
                isUploadingAttachments: isUploadingAttachments,
                hideDropdownActions: hideDropdownActions
            })
        ]
    });
}
